import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Grid,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Divider,
  IconButton,
  InputAdornment,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import {
  Save,
  Psychology,
  Visibility,
  VisibilityOff,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { configurationService } from '../../services/configurationService';
import ImageUploadCrop from '../../components/common/ImageUploadCrop';
import ChamadoAssuntoSelect from '../../components/selects/ChamadoAssuntoSelect';
import EmailServerConfigSection from './EmailServerConfigSection';
import { formatarCPFCNPJ, formatarCPFCNPJOnInput } from '../../utils/formatToView';


interface ParametroGeral {
  timezone: string;
  google_maps_api_key: string;
  tokenOpenAi: string;
  criar_chamado_instalacao_contrato: string;
  status_novo_contrato: string;
  status_apos_assinatura_contrato: string;
  status_padrao_chamado_instalacao: string;
  smtp_host: string;
  smtp_port: string;
  smtp_secure: string;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
  smtp_tls_reject_unauthorized: string;
  restricao_cancelamento_contrato: string[];
  exigir_assinatura_contrato: boolean;
  auto_ip: boolean;
  auto_mac: boolean;
}


const timezones = [
  { value: 'America/Sao_Paulo', label: 'America/São_Paulo (UTC-3)' },
  { value: 'America/Manaus', label: 'America/Manaus (UTC-4)' },
  { value: 'America/Rio_Branco', label: 'America/Rio_Branco (UTC-5)' },
  { value: 'America/Fortaleza', label: 'America/Fortaleza (UTC-3)' },
  { value: 'America/Recife', label: 'America/Recife (UTC-3)' },
  { value: 'America/Bahia', label: 'America/Bahia (UTC-3)' }
];

const ParametrosGeral: React.FC = () => {
  const [parametros, setParametros] = useState<ParametroGeral>({
    timezone: 'America/Sao_Paulo',
    google_maps_api_key: '',
    tokenOpenAi: '',
    criar_chamado_instalacao_contrato: 'desabilitado',
    status_novo_contrato: 'aguardando_assinatura',
    status_apos_assinatura_contrato: 'aguardando_instalacao',
    status_padrao_chamado_instalacao: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_secure: 'false',
    smtp_user: '',
    smtp_pass: '',
    smtp_from: '',
    smtp_tls_reject_unauthorized: 'true',
    restricao_cancelamento_contrato: [],
    exigir_assinatura_contrato: false,
    auto_ip: false,
    auto_mac: false,
  });

  const [loading, setLoading] = useState(false);
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [showGoogleMapsKey, setShowGoogleMapsKey] = useState(false);
  const [showOpenAIToken, setShowOpenAIToken] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Estados para modais de ajuda
  const [helpModal, setHelpModal] = useState<'contrato' | 'tokens' | 'email' | null>(null);

  // Estados para logo
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [logoMenuAnchor, setLogoMenuAnchor] = useState<null | HTMLElement>(null);
  const [logoVersion, setLogoVersion] = useState(Date.now());

  // Estados para ícone da logo
  const [uploadIconModalOpen, setUploadIconModalOpen] = useState(false);
  const [iconLogoMenuAnchor, setIconLogoMenuAnchor] = useState<null | HTMLElement>(null);
  const [iconLogoVersion, setIconLogoVersion] = useState(Date.now());

  // Estados para dados do provedor
  const [provedorNome, setProvedorNome] = useState('');
  const [provedorSlogan, setProvedorSlogan] = useState('');
  const [provedorCnpj, setProvedorCnpj] = useState('');
  const [provedorEndereco, setProvedorEndereco] = useState('');
  const [savingProvedor, setSavingProvedor] = useState(false);
  const [updatingAppCliente, setUpdatingAppCliente] = useState(false);
  const [updatingAppTecnico, setUpdatingAppTecnico] = useState(false);

  // Carregar configurações do banco
  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await configurationService.getAllConfigurations();

      // Mapear configurações para o estado
      const configMap: { [key: string]: string } = {};
      configs.forEach(config => {
        configMap[config.chave] = config.valor;
      });

      setParametros({
        timezone: configMap['timezone'] || 'America/Sao_Paulo',
        google_maps_api_key: configMap['google_maps_api_key'] || '',
        tokenOpenAi: configMap['tokenOpenAi'] || '',
        criar_chamado_instalacao_contrato: configMap['criar_chamado_instalacao_contrato'] || 'desabilitado',
        status_novo_contrato: configMap['status_novo_contrato'] || 'aguardando_assinatura',
        status_apos_assinatura_contrato: configMap['status_apos_assinatura_contrato'] || 'aguardando_instalacao',
        status_padrao_chamado_instalacao: configMap['status_padrao_chamado_instalacao'] || '',
        smtp_host: configMap['smtp_host'] || '',
        smtp_port: configMap['smtp_port'] || '587',
        smtp_secure: configMap['smtp_secure'] === 'true' ? 'true' : 'false',
        smtp_user: configMap['smtp_user'] || '',
        smtp_pass: configMap['smtp_pass'] || '',
        smtp_from: configMap['smtp_from'] || '',
        smtp_tls_reject_unauthorized:
          configMap['smtp_tls_reject_unauthorized'] === 'false' ? 'false' : 'true',
        restricao_cancelamento_contrato: configMap['restricao_cancelamento_contrato']
          ? configMap['restricao_cancelamento_contrato'].split(',').filter(Boolean)
          : [],
        exigir_assinatura_contrato: configMap['exigir_assinatura_contrato'] === 'true' || configMap['exigir_assinatura_contrato'] === '1',
        auto_ip: configMap['auto_ip'] === 'true' || configMap['auto_ip'] === '1',
        auto_mac: configMap['auto_mac'] === 'true' || configMap['auto_mac'] === '1',
      });

      setProvedorNome(configMap['provedor_nome'] || '');
      setProvedorSlogan(configMap['provedor_slogan'] || '');
      setProvedorCnpj(formatarCPFCNPJ(configMap['provedor_cnpj'] || ''));
      setProvedorEndereco(configMap['provedor_endereco'] || '');

    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProvedor = async () => {
    setSavingProvedor(true);
    try {
      await configurationService.upsertConfiguration('provedor_nome', provedorNome, { descricao: 'Nome do Provedor exibido no AppCliente', categoria: 'central' });
      await configurationService.upsertConfiguration('provedor_slogan', provedorSlogan, { descricao: 'Slogan / Frase de Impacto da Empresa', categoria: 'central' });
      await configurationService.upsertConfiguration('provedor_cnpj', provedorCnpj, { descricao: 'CNPJ do Provedor exibido no AppCliente', categoria: 'central' });
      await configurationService.upsertConfiguration('provedor_endereco', provedorEndereco, { descricao: 'Endereço do Provedor exibido no AppCliente', categoria: 'central' });
      toast.success('Dados do provedor salvos com sucesso!');
      setMessage({ type: 'success', text: 'Dados do provedor salvos com sucesso!' });
    } catch (error) {
      console.error('Erro ao salvar dados do provedor:', error);
      toast.error('Erro ao salvar dados do provedor.');
      setMessage({ type: 'error', text: 'Erro ao salvar dados do provedor.' });
    } finally {
      setSavingProvedor(false);
    }
  };

  const handleAtualizarAppCliente = async () => {
    setUpdatingAppCliente(true);
    try {
      const response = await configurationService.updateAppClienteBranding(provedorNome);
      if (response.success) {
        toast.success(response.message || 'AppCliente atualizado com sucesso!');
        setMessage({ type: 'success', text: response.message || 'AppCliente atualizado com sucesso!' });
      } else {
        toast.error(response.message || 'Erro ao atualizar AppCliente');
        setMessage({ type: 'error', text: response.message || 'Erro ao atualizar AppCliente' });
      }
    } catch (error: any) {
      console.error('Erro ao atualizar AppCliente:', error);
      toast.error(error.message || 'Erro ao atualizar AppCliente');
      setMessage({ type: 'error', text: error.message || 'Erro ao atualizar AppCliente' });
    } finally {
      setUpdatingAppCliente(false);
    }
  };

  const handleAtualizarAppTecnico = async () => {
    setUpdatingAppTecnico(true);
    try {
      const response = await configurationService.updateAppTecnicoBranding(provedorNome);
      if (response.success) {
        toast.success(response.message || 'AppTécnico atualizado com sucesso!');
        setMessage({ type: 'success', text: response.message || 'AppTécnico atualizado com sucesso!' });
      } else {
        toast.error(response.message || 'Erro ao atualizar AppTécnico');
        setMessage({ type: 'error', text: response.message || 'Erro ao atualizar AppTécnico' });
      }
    } catch (error: any) {
      console.error('Erro ao atualizar AppTécnico:', error);
      toast.error(error.message || 'Erro ao atualizar AppTécnico');
      setMessage({ type: 'error', text: error.message || 'Erro ao atualizar AppTécnico' });
    } finally {
      setUpdatingAppTecnico(false);
    }
  };

  const getLogoUrl = () => {
    return configurationService.getLogoUrl();
  };

  const getLogoUrlSysup = () => {
    return configurationService.getLogoUrlSysup();
  };

  const handleLogoUpload = () => {
    setUploadModalOpen(true);
    setLogoMenuAnchor(null);
  };

  const getIconLogoUrl = () => {
    return configurationService.getIconLogoUrl();
  };

  const getIconLogoUrlSysup = () => {
    return configurationService.getIconLogoUrlSysup();
  };

  const handleIconLogoUpload = () => {
    setUploadIconModalOpen(true);
    setIconLogoMenuAnchor(null);
  };

  const handleIconImageSave = async (croppedImageBlob: Blob, croppedImageUrl: string) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `icon-logo.${extension}`, {
        type: croppedImageBlob.type
      });

      const response = await configurationService.uploadIconLogo(
        file,
        (progress) => setUploadProgress(progress)
      );

      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Ícone enviado com sucesso!' });
        setIconLogoVersion(Date.now());
      } else {
        setMessage({ type: 'error', text: response.message || 'Erro ao fazer upload do ícone' });
      }
    } catch (error) {
      console.error('Erro ao fazer upload do ícone:', error);
      setMessage({ type: 'error', text: 'Erro ao fazer upload do ícone' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageSave = async (croppedImageBlob: Blob, croppedImageUrl: string) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `company-logo.${extension}`, {
        type: croppedImageBlob.type
      });

      const response = await configurationService.uploadLogo(
        file,
        (progress) => setUploadProgress(progress)
      );

      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Logo enviada com sucesso!' });
        setLogoVersion(Date.now());
      } else {
        setMessage({ type: 'error', text: response.message || 'Erro ao fazer upload da logo' });
      }
    } catch (error) {
      console.error('Erro ao fazer upload da logo:', error);
      setMessage({ type: 'error', text: 'Erro ao fazer upload da logo' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleInputChange = (field: keyof ParametroGeral, value: any) => {
    setParametros(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage(null);

      // Mapear campos para chaves de configuração
      const configUpdates = [
        { chave: 'timezone', valor: parametros.timezone },
        { chave: 'google_maps_api_key', valor: parametros.google_maps_api_key },
        { chave: 'tokenOpenAi', valor: parametros.tokenOpenAi },
        { chave: 'criar_chamado_instalacao_contrato', valor: parametros.criar_chamado_instalacao_contrato },
        { chave: 'status_novo_contrato', valor: parametros.status_novo_contrato },
        { chave: 'status_apos_assinatura_contrato', valor: parametros.status_apos_assinatura_contrato },
        { chave: 'status_padrao_chamado_instalacao', valor: parametros.status_padrao_chamado_instalacao },
        { chave: 'restricao_cancelamento_contrato', valor: parametros.restricao_cancelamento_contrato.join(',') },
      ];

      // Atualizar cada configuração
      for (const config of configUpdates) {
        await configurationService.updateConfiguration(config.chave, { valor: config.valor });
      }

      const smtpUpserts: { chave: string; valor: string; descricao: string }[] = [
        { chave: 'smtp_host', valor: parametros.smtp_host, descricao: 'Host do servidor SMTP' },
        { chave: 'smtp_port', valor: parametros.smtp_port || '587', descricao: 'Porta SMTP' },
        { chave: 'smtp_secure', valor: parametros.smtp_secure, descricao: 'SMTP SSL/TLS direto (porta segura)' },
        { chave: 'smtp_user', valor: parametros.smtp_user, descricao: 'Usuário SMTP' },
        { chave: 'smtp_pass', valor: parametros.smtp_pass, descricao: 'Senha SMTP' },
        { chave: 'smtp_from', valor: parametros.smtp_from, descricao: 'E-mail remetente padrão' },
        {
          chave: 'smtp_tls_reject_unauthorized',
          valor: parametros.smtp_tls_reject_unauthorized,
          descricao: 'TLS: rejeitar certificados não confiáveis',
        },
      ];
      for (const row of smtpUpserts) {
        await configurationService.upsertConfiguration(row.chave, row.valor, {
          descricao: row.descricao,
          categoria: 'email',
        });
      }

      await configurationService.upsertConfiguration('exigir_assinatura_contrato', String(parametros.exigir_assinatura_contrato), {
        descricao: 'Exigir assinatura de contrato na central do assinante',
        categoria: 'central',
        tipo: 'boolean',
      });

      await configurationService.upsertConfiguration('auto_ip', String(parametros.auto_ip), {
        descricao: 'Preenchimento automático do IP com base na conexão RADIUS',
        categoria: 'contrato',
        tipo: 'boolean',
      });

      await configurationService.upsertConfiguration('auto_mac', String(parametros.auto_mac), {
        descricao: 'Preenchimento automático do MAC com base na conexão RADIUS',
        categoria: 'contrato',
        tipo: 'boolean',
      });

      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      setMessage({ type: 'error', text: error.message || 'Erro ao salvar configurações' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestOpenAI = async () => {
    try {
      setTestingOpenAI(true);
      setMessage(null);

      // Primeiro salvar o token se foi alterado
      if (parametros.tokenOpenAi) {
        await configurationService.updateConfiguration('tokenOpenAi', { valor: parametros.tokenOpenAi });
      }

      // Testar o token
      const result = await configurationService.testOpenAI();
      setMessage({ type: 'success', text: result.message });
    } catch (error: any) {
      console.error('Erro ao testar OpenAI:', error);
      setMessage({ type: 'error', text: error.message || 'Erro ao testar token OpenAI' });
    } finally {
      setTestingOpenAI(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      setTestingSmtp(true);
      setMessage(null);
      const result = await configurationService.testSmtp({
        smtp_host: parametros.smtp_host,
        smtp_port: parametros.smtp_port,
        smtp_secure: parametros.smtp_secure,
        smtp_user: parametros.smtp_user,
        smtp_pass: parametros.smtp_pass,
        smtp_from: parametros.smtp_from,
        smtp_tls_reject_unauthorized: parametros.smtp_tls_reject_unauthorized,
      });
      setMessage({
        type: result.success ? 'success' : 'error',
        text: result.message || (result.success ? 'SMTP OK' : 'Falha no teste SMTP'),
      });
    } catch (error: any) {
      console.error('Erro ao testar SMTP:', error);
      setMessage({ type: 'error', text: error.message || 'Erro ao testar SMTP' });
    } finally {
      setTestingSmtp(false);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h6" gutterBottom>
        Configurações Gerais
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={2}>

        {/* Logo da Empresa */}
        <Grid item xs={8}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <img
              src={`${getLogoUrl()}?v=${logoVersion}`}
              style={{
                width: 220,
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onError={(e) => {
                e.currentTarget.src = getLogoUrlSysup();
              }}
              onClick={(e) => setLogoMenuAnchor(e.currentTarget)}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Logo da Empresa
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clique na imagem para alterar. Esta logo será usada na impressão de faturas e boletos.
              </Typography>
            </Box>

            {/* Menu de contexto da logo */}
            <Menu
              anchorEl={logoMenuAnchor}
              open={Boolean(logoMenuAnchor)}
              onClose={() => setLogoMenuAnchor(null)}
            >
              <MenuItem onClick={handleLogoUpload}>
                <ListItemIcon>
                  <PhotoCameraIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Alterar Logo</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Grid>

        {/* Ícone da Empresa */}
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <img
              src={`${getIconLogoUrl()}?v=${iconLogoVersion}`}
              style={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onError={(e) => {
                e.currentTarget.src = getIconLogoUrlSysup();
              }}
              onClick={(e) => setIconLogoMenuAnchor(e.currentTarget)}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Ícone da Logo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clique para alterar. É recomendado o formato quadrado (1:1).
              </Typography>
            </Box>

            {/* Menu de contexto do ícone da logo */}
            <Menu
              anchorEl={iconLogoMenuAnchor}
              open={Boolean(iconLogoMenuAnchor)}
              onClose={() => setIconLogoMenuAnchor(null)}
            >
              <MenuItem onClick={handleIconLogoUpload}>
                <ListItemIcon>
                  <PhotoCameraIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Alterar Ícone</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Grid>

        {/* Dados do Provedor */}
        <Grid item xs={12}>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Dados do Provedor
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Essas informações serão exibidas no rodapé e outras áreas dos aplicativos (AppCliente e AppTécnico).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Nome do Provedor (Nome Fantasia)"
                  size="small"
                  value={provedorNome}
                  onChange={(e) => setProvedorNome(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Slogan / Frase de Impacto da Empresa"
                  size="small"
                  placeholder="A internet ultrarrápida que conecta a sua vida"
                  value={provedorSlogan}
                  onChange={(e) => setProvedorSlogan(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="CNPJ"
                  size="small"
                  placeholder="00.000.000/0000-00"
                  value={provedorCnpj}
                  onChange={(e) => setProvedorCnpj(formatarCPFCNPJOnInput(e.target.value))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço Completo"
                  size="small"
                  value={provedorEndereco}
                  onChange={(e) => setProvedorEndereco(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={updatingAppCliente ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  onClick={handleAtualizarAppCliente}
                  disabled={updatingAppCliente || updatingAppTecnico || savingProvedor}
                  size="small"
                >
                  {updatingAppCliente ? 'Atualizando APP...' : 'Atualizar App Cliente'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={updatingAppTecnico ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  onClick={handleAtualizarAppTecnico}
                  disabled={updatingAppCliente || updatingAppTecnico || savingProvedor}
                  size="small"
                >
                  {updatingAppTecnico ? 'Atualizando APP...' : 'Atualizar App Técnico'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={savingProvedor ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSaveProvedor}
                  disabled={savingProvedor || updatingAppCliente || updatingAppTecnico}
                  size="small"
                >
                  Salvar Dados
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
              Tokens
            </Typography>
            <Tooltip title="Como funciona esta seção?">
              <IconButton size="small" color="primary" onClick={() => setHelpModal('tokens')} sx={{ p: 0.25 }}>
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Timezone */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Fuso Horário</InputLabel>
            <Select
              value={parametros.timezone}
              label="Fuso Horário"
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              size="small"
            >
              {timezones.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Google Maps API Key */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Google Maps API Key"
            type={showGoogleMapsKey ? 'text' : 'password'}
            value={parametros.google_maps_api_key}
            onChange={(e) => handleInputChange('google_maps_api_key', e.target.value)}
            placeholder="Chave da API do Google Maps"
            //helperText="Necessário para funcionalidades de mapa"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowGoogleMapsKey(!showGoogleMapsKey)}
                    edge="end"
                  >
                    {showGoogleMapsKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Grid>

        {/* Token OpenAI */}
        <Grid item xs={12} md={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Token OpenAI"
              type={showOpenAIToken ? 'text' : 'password'}
              value={parametros.tokenOpenAi}
              onChange={(e) => handleInputChange('tokenOpenAi', e.target.value)}
              placeholder="Token de acesso da API OpenAI"
              //helperText="Necessário para funcionalidades de IA"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowOpenAIToken(!showOpenAIToken)}
                      edge="end"
                    >
                      {showOpenAIToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Psychology />}
              onClick={handleTestOpenAI}
              disabled={testingOpenAI || !parametros.tokenOpenAi}
              size="small"
            >
              {testingOpenAI ? 'Testando...' : 'Testar'}
            </Button>
          </Box>
        </Grid>

        <EmailServerConfigSection
          values={{
            smtp_host: parametros.smtp_host,
            smtp_port: parametros.smtp_port,
            smtp_secure: parametros.smtp_secure,
            smtp_user: parametros.smtp_user,
            smtp_pass: parametros.smtp_pass,
            smtp_from: parametros.smtp_from,
            smtp_tls_reject_unauthorized: parametros.smtp_tls_reject_unauthorized,
          }}
          onChange={(field, value) => handleInputChange(field as keyof ParametroGeral, value)}
          onTest={handleTestSmtp}
          testingSmtp={testingSmtp}
          disabled={loading}
          onHelpClick={() => setHelpModal('email')}
          showSmtpPass={showSmtpPass}
          onToggleSmtpPass={() => setShowSmtpPass(!showSmtpPass)}
        />

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
              Contrato
            </Typography>
            <Tooltip title="Como funciona esta seção?">
              <IconButton size="small" color="primary" onClick={() => setHelpModal('contrato')} sx={{ p: 0.25 }}>
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Status do novo contrato */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status do novo contrato</InputLabel>
            <Select
              value={parametros.status_novo_contrato}
              label="Status do novo contrato"
              onChange={(e) => handleInputChange('status_novo_contrato', e.target.value)}
              size="small"
            >
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="aguardando_assinatura">Aguardando assinatura</MenuItem>
              <MenuItem value="aguardando_instalacao">Aguardando instalação</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Status após assinatura do contrato */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status após assinatura do contrato</InputLabel>
            <Select
              value={parametros.status_apos_assinatura_contrato}
              label="Status após assinatura do contrato"
              onChange={(e) => handleInputChange('status_apos_assinatura_contrato', e.target.value)}
              size="small"
            >
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="aguardando_instalacao">Aguardando instalação</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Criar chamado de Instalação */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Criar chamado de Instalação</InputLabel>
            <Select
              value={parametros.criar_chamado_instalacao_contrato}
              label="Criar chamado de Instalação"
              onChange={(e) => handleInputChange('criar_chamado_instalacao_contrato', e.target.value)}
              size="small"
            >
              <MenuItem value="desabilitado">Desabilitado</MenuItem>
              <MenuItem value="apos_novo_contrato">Após novo contrato</MenuItem>
              <MenuItem value="apos_assinatura_contrato">Após assinatura do contrato</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Assunto Padrão de Instalação */}
        <Grid item xs={12} md={3}>
          <ChamadoAssuntoSelect
            value={parametros.status_padrao_chamado_instalacao ? parseInt(parametros.status_padrao_chamado_instalacao) : null}
            onChange={(id) => handleInputChange('status_padrao_chamado_instalacao', id ? String(id) : '')}
            label="Assunto padrão p/ nova instalação"
            placeholder="Selecione o assunto"
          />
        </Grid>

        {/* Restrição de Cancelamento */}
        <Grid item xs={12} md={5}>
          <FormControl fullWidth size="small">
            <InputLabel>Proibir cancelamento se houver pendências</InputLabel>
            <Select
              multiple
              value={parametros.restricao_cancelamento_contrato}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange('restricao_cancelamento_contrato', typeof value === 'string' ? value.split(',') : value);
              }}
              input={<OutlinedInput label="Proibir cancelamento se houver pendências" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={
                      value === 'financeiro' ? 'Financeiro' :
                        value === 'equipamentos' ? 'Equipamentos' :
                          value === 'chamados' ? 'Chamados' : value
                    } size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="financeiro">Financeiro</MenuItem>
              <MenuItem value="equipamentos">Equipamentos</MenuItem>
              <MenuItem value="chamados">Chamados</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Exigir assinatura de Contrato */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.exigir_assinatura_contrato}
                onChange={(e) => handleInputChange('exigir_assinatura_contrato', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="500">
                  Exigir assinatura de Contrato
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Exige assinatura na central do assinante.
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Auto IP */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.auto_ip}
                onChange={(e) => handleInputChange('auto_ip', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="500">
                  Auto IP
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preenchimento automático do IP com base na conexão RADIUS.
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Auto MAC */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.auto_mac}
                onChange={(e) => handleInputChange('auto_mac', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="500">
                  Auto MAC
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preenchimento automático do MAC com base na conexão RADIUS.
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Modal de Ajuda - Tokens */}
      <Dialog open={helpModal === 'tokens'} onClose={() => setHelpModal(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" /> Tokens e Integrações
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Esta seção configura as chaves e tokens necessários para integrações externas do sistema.
          </Typography>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>Campos da seção</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">1.</Typography></ListItemIcon>
              <ListItemText
                primary="Fuso Horário"
                secondary="Define o fuso horário utilizado em todo o sistema para datas, horários de corte e bloqueio. Recomenda-se usar o fuso da sua região (ex: America/São_Paulo para UTC-3)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">2.</Typography></ListItemIcon>
              <ListItemText
                primary="Google Maps API Key"
                secondary="Chave da API do Google Maps. Necessária para funcionalidades de mapa, geolocalização e autocomplete de endereços. Obtenha em console.cloud.google.com."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">3.</Typography></ListItemIcon>
              <ListItemText
                primary="Token OpenAI"
                secondary="Token de acesso da API OpenAI para funcionalidades de IA (ex: assistente, sugestões). Use o botão 'Testar' para validar se o token está correto, adquira um token em openai.com."
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModal(null)} color="primary" variant="contained">Entendi</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Ajuda - Servidor de Email */}
      <Dialog open={helpModal === 'email'} onClose={() => setHelpModal(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" /> Servidor de Email (SMTP)
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Os dados salvos aqui são usados pelo backend para enviar e-mails (Nodemailer). Se algum campo estiver vazio no teste, o servidor pode completar com o que já está gravado no banco ou com variáveis de ambiente (SMTP_HOST, SMTP_PORT, etc.).
          </Typography>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
            Campos
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">1.</Typography></ListItemIcon>
              <ListItemText
                primary="Host e porta"
                secondary="Servidor e porta SMTP do seu provedor (ex.: Gmail, Outlook, servidor próprio). Porta 587 costuma usar STARTTLS; 465 costuma usar SSL/TLS direto (ative o switch correspondente)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">2.</Typography></ListItemIcon>
              <ListItemText
                primary="Usuário e senha"
                secondary="Credenciais da conta. Em Gmail/Google Workspace use senha de app, não a senha da conta comum."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">3.</Typography></ListItemIcon>
              <ListItemText
                primary="E-mail remetente"
                secondary="Endereço exibido como remetente. Alguns provedores exigem que coincida com a conta autenticada."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">4.</Typography></ListItemIcon>
              <ListItemText
                primary="TLS: rejeitar certificados inválidos"
                secondary="Mantenha ativado em produção. Desative apenas para diagnóstico em ambientes com certificado autoassinado."
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModal(null)} color="primary" variant="contained">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Ajuda - Contrato */}
      <Dialog open={helpModal === 'contrato'} onClose={() => setHelpModal(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" /> Configurações de Contrato
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Define o fluxo de status dos contratos e quando criar chamados de instalação automaticamente.
          </Typography>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>Campos da seção</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">1.</Typography></ListItemIcon>
              <ListItemText
                primary="Status do novo contrato"
                secondary="Status inicial ao criar um novo contrato. Opções: Ativo (já liberado), Aguardando assinatura (cliente precisa assinar digitalmente) ou Aguardando instalação."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">2.</Typography></ListItemIcon>
              <ListItemText
                primary="Status após assinatura do contrato"
                secondary="Status que o contrato assume após o cliente assinar digitalmente pelo App. Geralmente 'Aguardando instalação' para que a equipe realize a instalação."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">3.</Typography></ListItemIcon>
              <ListItemText
                primary="Criar chamado de Instalação"
                secondary="Quando criar chamado automaticamente: Desabilitado (nunca), Após novo contrato (ao cadastrar) ou Após assinatura do contrato (quando o cliente assinar)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">4.</Typography></ListItemIcon>
              <ListItemText
                primary="Assunto padrão para chamado de instalação"
                secondary="Define qual o assunto (tipo) de chamado será utilizado quando o sistema criar um chamado de instalação automaticamente."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">5.</Typography></ListItemIcon>
              <ListItemText
                primary="Auto IP"
                secondary="Preenchimento automático do campo endereço IP nos serviços de internet que estiverem sem IP, utilizando o IP da última conexão registrada no FreeRADIUS."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography color="primary" fontWeight="bold">6.</Typography></ListItemIcon>
              <ListItemText
                primary="Auto MAC"
                secondary="Preenchimento automático do campo endereço MAC nos serviços de internet que estiverem sem MAC, utilizando o MAC da última conexão registrada no FreeRADIUS."
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModal(null)} color="primary" variant="contained">Entendi</Button>
        </DialogActions>
      </Dialog>


      {/* Modal de Upload de Logo */}
      <ImageUploadCrop
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onImageSave={handleImageSave}
        title="Upload de Logo da Empresa"
        aspectRatio={16 / 6}
        maxWidth={800}
        maxHeight={450}
        quality={0.9}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={100}
      />

      {/* Modal de Upload de Ícone da Logo */}
      <ImageUploadCrop
        open={uploadIconModalOpen}
        onClose={() => setUploadIconModalOpen(false)}
        onImageSave={handleIconImageSave}
        title="Upload de Ícone da Empresa"
        aspectRatio={1 / 1}
        maxWidth={512}
        maxHeight={512}
        quality={0.9}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={50}
      />
    </Box>
  );
};

export default ParametrosGeral; 